import { request } from '~/services/common';

type RequestOptions = {
  signal?: AbortSignal;
  [key: string]: unknown;
};

/**
 * Keeps a background read from leaving navigation in a permanent loading state.
 * The caller's query signal still wins when the view unmounts; only this
 * helper's own timer changes the error into a useful retry message.
 */
export async function requestWithTimeout<T>(
  destination: string,
  options: RequestOptions = {},
  timeoutMessage = 'This is taking too long. Please try again.',
  timeoutMs = 12_000,
): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  options.signal?.addEventListener('abort', abortFromCaller, { once: true });
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return (await request(destination, { ...options, signal: controller.signal })) as T;
  } catch (error) {
    if (timedOut) {
      throw new Error(timeoutMessage, { cause: error });
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    options.signal?.removeEventListener('abort', abortFromCaller);
  }
}
